/**
 * @typedef Options
 *   Configuration (optional).
 * @property {string|null|Array<string|null|undefined>} [align]
 *   One style for all columns, or styles for their respective columns.
 *   Each style is either `'l'` (left), `'r'` (right), or `'c'` (center).
 *   Other values are treated as `''`, which doesn’t place the colon in the
 *   alignment row but does align left.
 *   *Only the lowercased first character is used, so `Right` is fine.*
 * @property {boolean} [padding=true]
 *   Whether to add a space of padding between delimiters and cells.
 *
 *   When `true`, there is padding:
 *
 *   ```markdown
 *   | Alpha | B     |
 *   | ----- | ----- |
 *   | C     | Delta |
 *   ```
 *
 *   When `false`, there is no padding:
 *
 *   ```markdown
 *   |Alpha|B    |
 *   |-----|-----|
 *   |C    |Delta|
 *   ```
 * @property {boolean} [delimiterStart=true]
 *   Whether to begin each row with the delimiter.
 *
 *   > 👉 **Note**: please don’t use this: it could create fragile structures
 *   > that aren’t understandable to some markdown parsers.
 *
 *   When `true`, there are starting delimiters:
 *
 *   ```markdown
 *   | Alpha | B     |
 *   | ----- | ----- |
 *   | C     | Delta |
 *   ```
 *
 *   When `false`, there are no starting delimiters:
 *
 *   ```markdown
 *   Alpha | B     |
 *   ----- | ----- |
 *   C     | Delta |
 *   ```
 * @property {boolean} [delimiterEnd=true]
 *   Whether to end each row with the delimiter.
 *
 *   > 👉 **Note**: please don’t use this: it could create fragile structures
 *   > that aren’t understandable to some markdown parsers.
 *
 *   When `true`, there are ending delimiters:
 *
 *   ```markdown
 *   | Alpha | B     |
 *   | ----- | ----- |
 *   | C     | Delta |
 *   ```
 *
 *   When `false`, there are no ending delimiters:
 *
 *   ```markdown
 *   | Alpha | B
 *   | ----- | -----
 *   | C     | Delta
 *   ```
 * @property {boolean} [alignDelimiters=true]
 *   Whether to align the delimiters.
 *   By default, they are aligned:
 *
 *   ```markdown
 *   | Alpha | B     |
 *   | ----- | ----- |
 *   | C     | Delta |
 *   ```
 *
 *   Pass `false` to make them staggered:
 *
 *   ```markdown
 *   | Alpha | B |
 *   | - | - |
 *   | C | Delta |
 *   ```
 * @property {(value: string) => number} [stringLength]
 *   Function to detect the length of table cell content.
 *   This is used when aligning the delimiters (`|`) between table cells.
 *   Full-width characters and emoji mess up delimiter alignment when viewing
 *   the markdown source.
 *   To fix this, you can pass this function, which receives the cell content
 *   and returns its “visible” size.
 *   Note that what is and isn’t visible depends on where the text is displayed.
 *
 *   Without such a function, the following:
 *
 *   ```js
 *   markdownTable([
 *     ['Alpha', 'Bravo'],
 *     ['中文', 'Charlie'],
 *     ['👩‍❤️‍👩', 'Delta']
 *   ])
 *   ```
 *
 *   Yields:
 *
 *   ```markdown
 *   | Alpha | Bravo |
 *   | - | - |
 *   | 中文 | Charlie |
 *   | 👩‍❤️‍👩 | Delta |
 *   ```
 *
 *   With [`string-width`](https://github.com/sindresorhus/string-width):
 *
 *   ```js
 *   import stringWidth from 'string-width'
 *
 *   markdownTable(
 *     [
 *       ['Alpha', 'Bravo'],
 *       ['中文', 'Charlie'],
 *       ['👩‍❤️‍👩', 'Delta']
 *     ],
 *     {stringLength: stringWidth}
 *   )
 *   ```
 *
 *   Yields:
 *
 *   ```markdown
 *   | Alpha | Bravo   |
 *   | ----- | ------- |
 *   | 中文  | Charlie |
 *   | 👩‍❤️‍👩    | Delta   |
 *   ```
 */

/**
 * @typedef {Options} MarkdownTableOptions
 * @todo
 *   Remove next major.
 */

/**
 * Generate a markdown ([GFM](https://docs.github.com/en/github/writing-on-github/working-with-advanced-formatting/organizing-information-with-tables)) table..
 *
 * @param {Array<Array<string|null|undefined>>} table
 *   Table data (matrix of strings).
 * @param {Options} [options]
 *   Configuration (optional).
 * @returns {string}
 */
export function markdownTable(table) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var align = (options.align || []).concat();
  var stringLength = options.stringLength || defaultStringLength;
  /** @type {Array<number>} Character codes as symbols for alignment per column. */

  var alignments = [];
  /** @type {Array<Array<string>>} Cells per row. */

  var cellMatrix = [];
  /** @type {Array<Array<number>>} Sizes of each cell per row. */

  var sizeMatrix = [];
  /** @type {Array<number>} */

  var longestCellByColumn = [];
  var mostCellsPerRow = 0;
  var rowIndex = -1; // This is a superfluous loop if we don’t align delimiters, but otherwise we’d
  // do superfluous work when aligning, so optimize for aligning.

  while (++rowIndex < table.length) {
    /** @type {Array<string>} */
    var _row = [];
    /** @type {Array<number>} */

    var _sizes = [];

    var _columnIndex = -1;

    if (table[rowIndex].length > mostCellsPerRow) {
      mostCellsPerRow = table[rowIndex].length;
    }

    while (++_columnIndex < table[rowIndex].length) {
      var cell = serialize(table[rowIndex][_columnIndex]);

      if (options.alignDelimiters !== false) {
        var size = stringLength(cell);
        _sizes[_columnIndex] = size;

        if (longestCellByColumn[_columnIndex] === undefined || size > longestCellByColumn[_columnIndex]) {
          longestCellByColumn[_columnIndex] = size;
        }
      }

      _row.push(cell);
    }

    cellMatrix[rowIndex] = _row;
    sizeMatrix[rowIndex] = _sizes;
  } // Figure out which alignments to use.


  var columnIndex = -1;

  if (typeof align === 'object' && 'length' in align) {
    while (++columnIndex < mostCellsPerRow) {
      alignments[columnIndex] = toAlignment(align[columnIndex]);
    }
  } else {
    var code = toAlignment(align);

    while (++columnIndex < mostCellsPerRow) {
      alignments[columnIndex] = code;
    }
  } // Inject the alignment row.


  columnIndex = -1;
  /** @type {Array<string>} */

  var row = [];
  /** @type {Array<number>} */

  var sizes = [];

  while (++columnIndex < mostCellsPerRow) {
    var _code = alignments[columnIndex];
    var before = '';
    var after = '';

    if (_code === 99
    /* `c` */
    ) {
      before = ':';
      after = ':';
    } else if (_code === 108
    /* `l` */
    ) {
      before = ':';
    } else if (_code === 114
    /* `r` */
    ) {
      after = ':';
    } // There *must* be at least one hyphen-minus in each alignment cell.


    var _size = options.alignDelimiters === false ? 1 : Math.max(1, longestCellByColumn[columnIndex] - before.length - after.length);

    var _cell = before + '-'.repeat(_size) + after;

    if (options.alignDelimiters !== false) {
      _size = before.length + _size + after.length;

      if (_size > longestCellByColumn[columnIndex]) {
        longestCellByColumn[columnIndex] = _size;
      }

      sizes[columnIndex] = _size;
    }

    row[columnIndex] = _cell;
  } // Inject the alignment row.


  cellMatrix.splice(1, 0, row);
  sizeMatrix.splice(1, 0, sizes);
  rowIndex = -1;
  /** @type {Array<string>} */

  var lines = [];

  while (++rowIndex < cellMatrix.length) {
    var _row2 = cellMatrix[rowIndex];
    var _sizes2 = sizeMatrix[rowIndex];
    columnIndex = -1;
    /** @type {Array<string>} */

    var line = [];

    while (++columnIndex < mostCellsPerRow) {
      var _cell2 = _row2[columnIndex] || '';

      var _before = '';
      var _after = '';

      if (options.alignDelimiters !== false) {
        var _size2 = longestCellByColumn[columnIndex] - (_sizes2[columnIndex] || 0);

        var _code2 = alignments[columnIndex];

        if (_code2 === 114
        /* `r` */
        ) {
          _before = ' '.repeat(_size2);
        } else if (_code2 === 99
        /* `c` */
        ) {
          if (_size2 % 2) {
            _before = ' '.repeat(_size2 / 2 + 0.5);
            _after = ' '.repeat(_size2 / 2 - 0.5);
          } else {
            _before = ' '.repeat(_size2 / 2);
            _after = _before;
          }
        } else {
          _after = ' '.repeat(_size2);
        }
      }

      if (options.delimiterStart !== false && !columnIndex) {
        line.push('|');
      }

      if (options.padding !== false && // Don’t add the opening space if we’re not aligning and the cell is
      // empty: there will be a closing space.
      !(options.alignDelimiters === false && _cell2 === '') && (options.delimiterStart !== false || columnIndex)) {
        line.push(' ');
      }

      if (options.alignDelimiters !== false) {
        line.push(_before);
      }

      line.push(_cell2);

      if (options.alignDelimiters !== false) {
        line.push(_after);
      }

      if (options.padding !== false) {
        line.push(' ');
      }

      if (options.delimiterEnd !== false || columnIndex !== mostCellsPerRow - 1) {
        line.push('|');
      }
    }

    lines.push(options.delimiterEnd === false ? line.join('').replace(/ +$/, '') : line.join(''));
  }

  return lines.join('\n');
}
/**
 * @param {string|null|undefined} [value]
 * @returns {string}
 */

function serialize(value) {
  return value === null || value === undefined ? '' : String(value);
}
/**
 * @param {string} value
 * @returns {number}
 */


function defaultStringLength(value) {
  return value.length;
}
/**
 * @param {string|null|undefined} value
 * @returns {number}
 */


function toAlignment(value) {
  var code = typeof value === 'string' ? value.codePointAt(0) : 0;
  return code === 67
  /* `C` */
  || code === 99
  /* `c` */
  ? 99
  /* `c` */
  : code === 76
  /* `L` */
  || code === 108
  /* `l` */
  ? 108
  /* `l` */
  : code === 82
  /* `R` */
  || code === 114
  /* `r` */
  ? 114
  /* `r` */
  : 0;
}